import React from "react";
import "../../styles/TagGroup.css";

export interface TagItem {
  id?: string | number;
  label: string;
  count?: number;
  dotColor?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  hoverTextColor?: string;
  disabledBackgroundColor?: string;
  disabledBorderColor?: string;
  disabledTextColor?: string;
  disabledDotColor?: string;
  disabled?: boolean;
  selected?: boolean;
  themeCategoryKey?: string;
}

export interface TagStyleConfig {
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  hoverBackgroundColor?: string;
  hoverBorderColor?: string;
  hoverTextColor?: string;
}

export interface TagGroupProps {
  tags: TagItem[];
  showTotalTag?: boolean;
  totalCount?: number;
  totalTagLabel?: string;
  totalTagStyle?: TagStyleConfig;
  onTagClick?: (tag: TagItem, index: number) => void;
  onTotalTagClick?: () => void;
  className?: string;
  gap?: number | string;
}

const TagGroup: React.FC<TagGroupProps> = ({
  tags = [],
  showTotalTag = true,
  totalCount,
  totalTagLabel = "Tags",
  totalTagStyle = {
    backgroundColor: "#F2F4F7",
    borderColor: "#D0D5DD",
    textColor: "#344054",
    hoverBackgroundColor: "#E4E7EC",
    hoverBorderColor: "#98A2B3",
    hoverTextColor: "#1D2939",
  },
  onTagClick,
  onTotalTagClick,
  className = "",
  gap,
}) => {
  // Calculate total count if not explicitly passed
  const computedTotalCount =
    totalCount !== undefined
      ? totalCount
      : tags.reduce((sum, tag) => sum + (tag.count || 0), 0);

  if (computedTotalCount === 0) {
    return null;
  }

  const containerClasses = ["tag-group-container", className].filter(Boolean).join(" ");

  return (
    <div
      className={containerClasses}
      style={{ gap: gap !== undefined ? gap : undefined }}
    >
      {/* Total Tag Pill */}
      {showTotalTag && computedTotalCount > 0 && (
        <div
          onClick={onTotalTagClick}
          className={`tag-group-total-pill ${
            onTotalTagClick ? "tag-group-total-pill--clickable" : ""
          }`}
          style={
            {
              "--tg-total-bg-color": totalTagStyle.backgroundColor || "#F2F4F7",
              "--tg-total-border-color": totalTagStyle.borderColor || "#D0D5DD",
              "--tg-total-text-color": totalTagStyle.textColor || "#344054",
              "--tg-total-hover-bg-color":
                totalTagStyle.hoverBackgroundColor || "#E4E7EC",
              "--tg-total-hover-border-color":
                totalTagStyle.hoverBorderColor || "#98A2B3",
              "--tg-total-hover-text-color":
                totalTagStyle.hoverTextColor || "#1D2939",
            } as React.CSSProperties
          }
        >
          {computedTotalCount} {totalTagLabel}
        </div>
      )}

      {/* Tag Pills */}
      {tags.map((tag, index) => {
        const {
          label,
          count,
          dotColor,
          backgroundColor,
          borderColor,
          textColor,
          hoverBackgroundColor,
          hoverBorderColor,
          hoverTextColor,
          disabledBackgroundColor,
          disabledBorderColor,
          disabledTextColor,
          disabledDotColor,
          disabled = false,
        } = tag;

        const defaultHoverBg = hoverBackgroundColor || backgroundColor;
        const defaultHoverBorder = hoverBorderColor || borderColor;
        const defaultHoverText = hoverTextColor || textColor;

        const isClickable = !disabled && Boolean(onTagClick);

        const pillClasses = [
          "tag-group-item",
          isClickable ? "tag-group-item--clickable" : "",
          disabled ? "tag-group-item--disabled" : "",
        ]
          .filter(Boolean)
          .join(" ");

        return (
          <div
            key={tag.id || `${label}-${index}`}
            onClick={(e) => {
              if (!disabled && onTagClick) {
                e.stopPropagation();
                onTagClick(tag, index);
              }
            }}
            className={pillClasses}
            style={
              {
                "--tg-bg-color": backgroundColor,
                "--tg-border-color": borderColor,
                "--tg-text-color": textColor,
                "--tg-hover-bg-color": defaultHoverBg,
                "--tg-hover-border-color": defaultHoverBorder,
                "--tg-hover-text-color": defaultHoverText,
                "--tg-disabled-bg-color": disabledBackgroundColor,
                "--tg-disabled-border-color": disabledBorderColor,
                "--tg-disabled-text-color": disabledTextColor,
                "--tg-disabled-dot-color": disabledDotColor,
                "--tg-dot-color": dotColor,
              } as React.CSSProperties
            }
          >
            {/* Color Dot */}
            {dotColor && <span className="tag-group-dot" />}

            {/* Label */}
            <span className="tag-group-label">{label}</span>

            {/* Count */}
            {count !== undefined && (
              <span className="tag-group-count">{count}</span>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default TagGroup;
